import { createElement, useMemo } from 'react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

// Build-time sanitizing plus a runtime allowlist. Fetched HTML never becomes
// executable page markup; supported document nodes become React elements.
const TAGS = new Set('a abbr article blockquote br caption code dd details div dl dt em h1 h2 h3 h4 h5 h6 hr kbd li ol p pre s section small span strong sub summary sup table tbody td th thead tr ul'.split(' '));
const DROPPED = new Set(['script', 'style', 'iframe', 'object', 'embed', 'form', 'input', 'button', 'svg', 'math']);
const ATTRS: Record<string, string> = { id: 'id', class: 'className', title: 'title', colspan: 'colSpan', rowspan: 'rowSpan', scope: 'scope', start: 'start', 'aria-label': 'aria-label' };

function renderNode(node: Node, key: string): ReactNode {
  if (node.nodeType === Node.TEXT_NODE) return node.textContent;
  if (node.nodeType !== Node.ELEMENT_NODE) return null;
  const element = node as Element;
  const tag = element.tagName.toLowerCase();
  if (DROPPED.has(tag)) return null;
  const children = Array.from(element.childNodes, (child, i) => renderNode(child, `${key}-${i}`));
  if (!TAGS.has(tag)) return children;
  const props: Record<string, unknown> = { key };
  for (const [attribute, name] of Object.entries(ATTRS)) {
    const value = element.getAttribute(attribute);
    if (value !== null) props[name] = value;
  }
  if (tag === 'a') {
    const href = element.getAttribute('href') ?? '';
    if (/^(https?:\/\/|mailto:|\/(?!\/)|#)/i.test(href)) {
      if (/^\/docs(?:\/|$|[?#])/.test(href) && !element.hasAttribute('download')) {
        return createElement(Link, { ...props, to: href }, ...children);
      }
      props.href = href;
      if (element.hasAttribute('download')) props.download = '';
      if (href.startsWith('https://')) props.rel = 'noreferrer';
    }
  }
  const result = createElement(tag, props, ...children);
  return tag === 'table'
    ? createElement('div', { key, className: 'table-scroll', tabIndex: 0, 'aria-label': 'Scrollable reference table' }, result)
    : result;
}

export function DocumentHtml({ html }: { html: string }) {
  const content = useMemo(() => {
    const document = new DOMParser().parseFromString(html, 'text/html');
    return Array.from(document.body.childNodes, (node, i) => renderNode(node, String(i)));
  }, [html]);
  return <div className="docs-content">{content}</div>;
}
